import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useFormik } from "formik"
import * as Yup from "yup"

// components
import { CButton, CImage } from '@coreui/react-pro'
import Select from 'react-select';
import AppCard from '../../components/AppCard'
import AppTable from 'src/components/AppTable'
import AppModalConfirmation from 'src/components/modal/AppModalConfirmation'

// icons
import AddMoreIconWhite from '../../assets/icons/AddMoreIconWhite.png'

// styling
import { styleButtonVars2 } from '../../components/custom-styles/buttonStyles'

// helpers
import Axios from '../../helpers/axios'
import { columnClientVoucherList } from '../../components/table-column/TableColumnList'
import { formUrlQuery } from '../../helpers/format-queries'
import { singleVoucherTypeOptionSelect, singleIsUsedOptionSelect } from '../../helpers/format-options'

const ClientVoucherList = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [showConfirmDelete, setShowConfirmDelete] = useState({ show: false, item: {} })
  const [showModalGenerateFile, setShowModalGenerateFile] = useState({ show: false })
  const [showModalExportFile, setShowModalExportFile] = useState({ show: false })

  const [clientVoucherList, setClientVoucherList] = useState([]);
  const [clientVoucherListCount, setClientVoucherListCount] = useState(0);
  const [clientVoucherListPerPage, setClientVoucherListPerPage] = useState(10);
  const [activeClientVoucherListPage, setActiveClientVoucherListPage] = useState(1);
  const [searchClientVoucherListFilter, setSearchClientVoucherListFilter] = useState("");
  const [columnClientVoucherListSorter, setColumnClientVoucherListSorter] = useState({ column: "createdAt", state: "desc" });
  const [columnFilter, setColumnFilter] = useState({})

  const [updateColumnList, setUpdateColumnList] = useState(columnClientVoucherList)

  const formikGenerate = useFormik({
    initialValues: {
      clientvoucher_type: null,
      clientvoucher_count: ''
    },
    validationSchema: Yup.object().shape({
      clientvoucher_type: Yup.object().shape({
        label: Yup.string().required('Please select voucher type.')
      }).required('Please select voucher type.'),
      clientvoucher_count: Yup.string()
        .required("Please enter count voucher."),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSaveGenerate(values)
      setSubmitting(false)
    },
  });

  const formikExport = useFormik({
    initialValues: {
      clientvoucher_type: null,
      clientvoucher_count: ''
    },
    validationSchema: Yup.object().shape({
      clientvoucher_type: Yup.object().shape({
        label: Yup.string().required('Please select voucher type.')
      }).required('Please select voucher type.'),
      clientvoucher_count: Yup.string()
        .required("Please enter count voucher."),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSaveExport(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    let queries = {
      search: searchClientVoucherListFilter,
      limit: clientVoucherListPerPage,
      offset: clientVoucherListPerPage * activeClientVoucherListPage - clientVoucherListPerPage,
      sort:
        (columnClientVoucherListSorter &&
          `${columnClientVoucherListSorter.column}-${columnClientVoucherListSorter.state}`) ||
        "",
    };
    if (columnFilter) {
      Object.keys(columnFilter).forEach((key) => {
        if (columnFilter[key]) {
          queries[key] = columnFilter[key].value
        } else {
          queries[key] = ''
        }
      })
    }
    queries = formUrlQuery(queries);
    fetchAllClientVouchers(queries);
  }, [
    searchClientVoucherListFilter,
    clientVoucherListPerPage,
    activeClientVoucherListPage,
    columnClientVoucherListSorter,
    columnFilter
  ]);

  useEffect(() => {
    let columnList = updateColumnList?.map((list) => {
      let data = list

      if (data.key == 'is_used') {
        data.filter = (_, onChange) => {
          return (
            <Select
              options={singleIsUsedOptionSelect()}
              value={columnFilter.is_used}
              onChange={(e) => {
                const selectedValue = e;
                onChange(selectedValue); // Ini yang penting! Meneruskan nilai ke SmartTable
                setColumnFilter((prevState) => ({
                  ...prevState,
                  is_used: selectedValue
                }));
              }}
              isClearable
            />
          )
        }
      }

      return data
    })

    setUpdateColumnList(columnList)
  }, [columnFilter])

  const handleSearchItems = (search) => {
    setActiveClientVoucherListPage(1)
    setSearchClientVoucherListFilter(search);
  }

  const fetchAllClientVouchers = (queries) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "get",
      url: `/api/gamepass/v1/voucher/client/list?${queries}`
    })
      .then(({ data }) => {
        const tempClientVoucherList = data?.value?.map((d) => ({
          ...d,
          is_used: d.is_used ? "Yes" : "No",
          is_game_pass: d.is_game_pass ? "Yes" : "No",
        }));

        setClientVoucherList(tempClientVoucherList);
        if (activeClientVoucherListPage === 1) {
          setClientVoucherListCount(data?.count);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  };

  const handleInputValueGenerate = (values) => {
    let data = {
      type: values.clientvoucher_type.value,
      count: values.clientvoucher_count
    }

    return data
  }

  const handleInputValueExport = (values) => {
    let data = {
      type: values.clientvoucher_type.value,
      count: values.clientvoucher_count
    }

    return data
  }

  const handlePostGenerate = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/gamepass/v1/voucher/client/generate`,
      data: payload,
      responseType: "blob",
    })
      .then(({ data }) => {
        if (data) {
          const fileBlob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
          const fileUrl = URL.createObjectURL(fileBlob);

          const link = document.createElement('a');
          link.href = fileUrl;
          link.download = `Client Code ${formikGenerate.values.clientvoucher_type.value === 'gamepass' ? 'GP' : 'MLWDP'}.xlsx`;

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(fileUrl);

          setTimeout(() => {
            setShowModalGenerateFile({ show: false })
            fetchAllClientVouchers()
            formikGenerate.resetForm()
            window.location.reload()
          }, 500)
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handlePostExport = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/gamepass/v1/voucher/client/export`,
      data: payload,
      responseType: "blob",
    })
      .then(({ data }) => {
        if (data) {
          const fileBlob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
          const fileUrl = URL.createObjectURL(fileBlob);

          const link = document.createElement('a');
          link.href = fileUrl;
          link.download = `Client Code ${formikExport.values.clientvoucher_type.value === 'gamepass' ? 'GP' : 'MLWDP'}.xlsx`;

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(fileUrl);

          setTimeout(() => {
            setShowModalExportFile({ show: false })
            formikExport.resetForm()
          }, 500)
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSaveGenerate = (values) => {
    const inputPayload = handleInputValueGenerate(values)
    handlePostGenerate(inputPayload)
  }

  const handleSaveExport= (values) => {
    const inputPayload = handleInputValueExport(values)
    handlePostExport(inputPayload)
  }

  const handleDeleteConfirmation = () => {
    Axios({
      method: "delete",
      url: `/api/gamepass/v1/voucher/client/delete/${showConfirmDelete?.item?.id}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          setShowConfirmDelete({ show: false, item: {} })
          fetchAllClientVouchers()
          window.location.reload()
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }

  const handleSelectVouchertype = (data, type) => {    
    if (data) {
      if (type == 'export') {
        formikExport.setValues((prevValues) => ({
         ...prevValues,
         clientvoucher_type: data
       }), false)
      } else {
        formikGenerate.setValues((prevValues) => ({
         ...prevValues,
         clientvoucher_type: data
       }), false)
      }
    }
  }

  const handleShowModalGenerate = () => {
    setShowModalGenerateFile({ show: !showModalGenerateFile.show })
  }

  const handleShowModalExport = () => {
    setShowModalExportFile({ show: !showModalExportFile.show })
  }

  return (
    <>
      {
        showModalGenerateFile?.show && (
          <AppModalConfirmation 
            confirmationType='clientvoucher-generate'
            showModal={showModalGenerateFile}
            setShowModal={setShowModalGenerateFile}
            values={formikGenerate.values}
            errors={formikGenerate.errors}
            handleChange={formikGenerate.handleChange}
            handleConfirmation={() => formikGenerate.handleSubmit()}
            headerTitle={"Please input voucher type and count"}
            handleSelectInput={handleSelectVouchertype}
            voucherTypeOptions={singleVoucherTypeOptionSelect()}
          />
        )
      }
      {
        showModalExportFile?.show && (
          <AppModalConfirmation 
            confirmationType='clientvoucher-export'
            showModal={showModalExportFile}
            setShowModal={setShowModalExportFile}
            values={formikExport.values}
            errors={formikExport.errors}
            handleChange={formikExport.handleChange}
            handleConfirmation={() => formikExport.handleSubmit()}
            headerTitle={"Please input voucher type and count"}
            handleSelectInput={handleSelectVouchertype}
            voucherTypeOptions={singleVoucherTypeOptionSelect()}
          />
        )
      }
      {
        showConfirmDelete?.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={showConfirmDelete?.item?.client_code}
            itemDetails={showConfirmDelete.item}
            itemType={showConfirmDelete.type}
            itemIndex={showConfirmDelete?.index}
            showModal={showConfirmDelete}
            setShowModal={setShowConfirmDelete}
            handleConfirmation={handleDeleteConfirmation}
          />
        )
      }
      <div className='d-grid gap-2 d-md-flex justify-content-md-end mb-3'>
        <CButton style={styleButtonVars2} onClick={handleShowModalExport}>
          Export
        </CButton>
        <CButton style={styleButtonVars2} onClick={handleShowModalGenerate}>
          Generate
        </CButton>
        <CButton style={styleButtonVars2} onClick={() => navigate('/client-voucher/add')}>
          <CImage fluid src={AddMoreIconWhite} className='me-2' />
          Add Client Voucher
        </CButton>
      </div>
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Client Voucher List</h4>}
        bodyContent={
          <AppTable
            clientVoucherListPage
            tableFilter
            setShowConfirmDelete={setShowConfirmDelete}
            tableColumns={updateColumnList}
            tableContent={clientVoucherList}
            contentPerPage={clientVoucherListPerPage}
            activeContentPage={activeClientVoucherListPage}
            contentCount={clientVoucherListCount}
            searchContentFilter={searchClientVoucherListFilter}
            setActiveContentPage={setActiveClientVoucherListPage}
            setSearchContentFilter={handleSearchItems}
            setContentPerPage={setClientVoucherListPerPage}
            setColumnContentSorter={setColumnClientVoucherListSorter}
            isShowColumnFilter={true}
          />
        }
      />
    </>
  )
}

export default ClientVoucherList